import { useCallback } from 'react';

import { Controller, useForm } from 'react-hook-form';

import { colors } from '../../../colors';
import { u } from '../../../styles/unit';
import { Checkbox } from '../../Checkbox/Checkbox';
import { CaretRightIcon } from '../../icons';
import { Box, Stack } from '../../Layout';
import { BaseSwingThreadInput, SwingThreadButton as Button } from '../components';

import * as css from './SwingThreadForm.css';

export type SwingThreadSelectOption = { label: string; value: string };

const NO_OPTION: SwingThreadSelectOption = { label: 'None', value: 'none' };

type FormValues = {
    message: string;
    nodeId?: SwingThreadSelectOption;
};

export type SwingThreadThreadDeletePayload = {
    id: number;
    swingUuid: string;
};

type SwingThreadFormProps = {
    options: SwingThreadSelectOption[];
    isSelectingParameter?: boolean;
    message?: string;
    selectedOption?: string;
    nodeId?: string | null;
    messageId?: number;
    isEditing?: boolean;
    onSubmitForm: (payload: { nodeId?: string; message: string; messageId?: number }) => void;
    onToggleParameterView?: () => void;
    onCancel?: () => void;
    onDeleteThread?: () => void;
};

export const SwingThreadForm = ({
    options,
    isSelectingParameter,
    message = '',
    messageId,
    nodeId,
    selectedOption,
    isEditing,
    onSubmitForm,
    onCancel,
    onDeleteThread,
    onToggleParameterView,
}: SwingThreadFormProps) => {
    const {
        control,
        register,
        handleSubmit,
        reset,
        formState: { isDirty },
    } = useForm<FormValues>({
        mode: 'onChange',
        defaultValues: { message, nodeId: { label: selectedOption, value: nodeId ?? undefined } },
    });

    const handleCancelClick = useCallback(() => {
        reset();
        onCancel?.();
    }, [reset, onCancel]);

    const handleSubmitForm = useCallback(
        (form: FormValues) => {
            onSubmitForm({ message: form.message, nodeId: form.nodeId?.value, messageId });
            if (isEditing) {
                onCancel?.();
            }
        },
        [messageId, onSubmitForm, isEditing, onCancel],
    );
    return (
        <>
            <hr className={css.divider} />
            <form onSubmit={handleSubmit(handleSubmitForm)} className={css.form}>
                {!isSelectingParameter && (
                    <>
                        <Controller
                            name="nodeId"
                            control={control}
                            render={({ field }) => (
                                <button className={css.nodeSelector} onClick={onToggleParameterView}>
                                    {field.value?.label ?? selectedOption ?? 'Select parameter'}
                                    <CaretRightIcon
                                        className={css.marginLeftAuto}
                                        width={24}
                                        height={24}
                                        color={colors.teal[700]}
                                    />
                                </button>
                            )}
                        />

                        <BaseSwingThreadInput
                            {...register('message', { required: true })}
                            noResize
                            placeholder="Message"
                            className={css.formTextArea}
                        />
                        <Box display="flex" justifyContent="space-between">
                            <div className={css.formActionSection}>
                                {onDeleteThread && message && (
                                    <Button variant="ghost" onClick={onDeleteThread}>
                                        Delete
                                    </Button>
                                )}
                            </div>

                            <div className={css.formActionSection}>
                                <Button onClick={handleCancelClick} variant="outlined">
                                    cancel
                                </Button>
                                <Button type="submit" disabled={!isDirty} variant="fill">
                                    send
                                </Button>
                            </div>
                        </Box>
                    </>
                )}
                {isSelectingParameter && (
                    <Controller
                        name="nodeId"
                        control={control}
                        render={({ field }) => (
                            <SwingThreadParameterSelection
                                options={options}
                                onChange={(option?: SwingThreadSelectOption) => {
                                    field.onChange(option);
                                    onToggleParameterView?.();
                                }}
                                value={field.value}
                            />
                        )}
                    />
                )}
            </form>
        </>
    );
};

type SwingThreadParameterSelectionProps = Pick<SwingThreadFormProps, 'options'> & {
    onChange: (value?: SwingThreadSelectOption) => void;
    value?: SwingThreadSelectOption;
};

const SwingThreadParameterSelection = ({ options, onChange, value }: SwingThreadParameterSelectionProps) => {
    return (
        <Stack gap={u(2)}>
            <Checkbox
                checked={NO_OPTION.value === value?.value}
                onChange={() => onChange(NO_OPTION.value === value?.value ? undefined : NO_OPTION)}
                secondary
                fullWidth
                label={NO_OPTION.label}
            />
            {options.map((option) => (
                <Checkbox
                    key={option.value}
                    checked={option.value === value?.value}
                    onChange={() => onChange(option.value === value?.value ? undefined : option)}
                    secondary
                    fullWidth
                    label={option.label}
                />
            ))}
        </Stack>
    );
};
